import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, Eye, CheckCircle2, XCircle, Loader2, User,
  Clock, AlertOctagon, FileText, Hash, Image as ImgIcon, RefreshCcw, Accessibility
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const STAGE_COLORS = {
  "pending-maker":       { bg: "rgba(245,158,11,0.10)",  border: "#f59e0b", text: "#fbbf24", label: "Pending Maker" },
  "pending-checker":     { bg: "rgba(59,130,246,0.10)",  border: "#3b82f6", text: "#60a5fa", label: "Pending Checker" },
  "operational":         { bg: "rgba(34,197,94,0.12)",   border: "#22c55e", text: "#4ade80", label: "Operational" },
  "rejected-by-maker":   { bg: "rgba(239,68,68,0.12)",   border: "#ef4444", text: "#fca5a5", label: "Rejected (Maker)" },
  "rejected-by-checker": { bg: "rgba(220,38,38,0.18)",   border: "#dc2626", text: "#fca5a5", label: "Rejected (Checker)" },
  "unknown":             { bg: "rgba(148,163,184,0.08)", border: "#475569", text: "#94a3b8", label: "—" },
};

function inferStage(c) {
  if (c.stage) return c.stage;
  if (!c.maker_decision) return "pending-maker";
  if (c.maker_decision === "reject") return "rejected-by-maker";
  if (!c.checker_decision) return "pending-checker";
  if (c.checker_decision === "reject") return "rejected-by-checker";
  return "operational";
}

function SignalBar({ label, score, weight, contribution }) {
  const pct = Math.min(100, Math.max(0, (1 - (score ?? 0)) * 100));
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="flex items-center" style={{ justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ color: "#cbd5e1", fontSize: 12, fontWeight: 500 }}>{label}</span>
        <span style={{ color: "#94a3b8", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>
          score {(score ?? 0).toFixed(2)} · w {weight} · contrib {(contribution ?? 0).toFixed(3)}
        </span>
      </div>
      <div style={{ height: 6, background: "#080c12", borderRadius: 999, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: pct > 70 ? "#22c55e" : pct > 40 ? "#f59e0b" : "#ef4444",
          transition: "width .3s ease",
        }} />
      </div>
    </div>
  );
}

export default function VerifyCasesPage() {
  const { user: me } = useAuth();
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escRef, setEscRef] = useState("");
  const [escSubReason, setEscSubReason] = useState("");
  const [escalating, setEscalating] = useState(false);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/cases`, {
        withCredentials: true,
        params: { status: statusFilter, limit: 100 },
      });
      setCases(data.cases || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load cases");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { void fetchCases(); }, [fetchCases]);

  async function openDetail(c) {
    setSelected(c);
    setDetail(null);
    setReason("");
    try {
      const { data } = await axios.get(`${API}/cases/${c.id}`, {
        withCredentials: true,
      });
      setDetail(data);
    } catch (e) {
      toast.error("Failed to load case detail");
      setSelected(null);
    }
  }

  async function decide(decisionKind) {
    if (!selected) return;
    if (reason.trim().length < 3) { toast.error("Reason is required (min 3 chars)"); return; }
    setActing(true);
    try {
      const { data } = await axios.post(`${API}/cases/${selected.id}/decision`,
        { decision: decisionKind, reason: reason.trim() },
        { withCredentials: true });
      const stageMsg = `Recorded → stage: ${data.stage}`;
      const ckycrMsg = data.ckycr?.kyc_identifier
        ? ` · CKYC ${data.ckycr.kyc_identifier} (${data.ckycr.idempotent ? "existing" : "new"}, MOCK)`
        : "";
      toast.success(stageMsg + ckycrMsg);
      setSelected(null);
      await fetchCases();
    } catch (e) {
      const detail = e.response?.data?.detail || "Decision failed";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setActing(false);
    }
  }

  async function submitEscalation() {
    if (escRef.trim().length < 2) { toast.error("Customer reference required"); return; }
    if (escSubReason.trim().length < 3) { toast.error("Sub-reason required (min 3 chars)"); return; }
    setEscalating(true);
    try {
      const { data } = await axios.post(`${API}/vcip/escalate-accessibility`, {
        customer_reference: escRef.trim(),
        sub_reason: escSubReason.trim(),
      }, { withCredentials: true });
      toast.success(`Escalated → case ${data.case_id.slice(0, 14)}… (reason: accessibility, not a liveness failure)`);
      setEscalateOpen(false);
      setEscRef(""); setEscSubReason("");
      setStatusFilter("pending");
      await fetchCases();
    } catch (e) {
      const detail = e.response?.data?.detail || "Escalation failed";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setEscalating(false);
    }
  }

  const stage = detail ? inferStage(detail) : null;
  const canIDecide = (() => {
    if (!detail || !me) return false;
    const st = inferStage(detail);
    if (st === "operational" || st === "rejected-by-maker" || st === "rejected-by-checker") return false;
    if (st === "pending-checker" && detail.maker_user_id === me.id) return false;   // Distinct-reviewer rule
    return true;
  })();

  return (
    <div style={{ maxWidth: 1320, margin: "0 auto", padding: 24 }}>
      {/* Header */}
      <div className="flex items-center" style={{ gap: 12, marginBottom: 6 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ShieldCheck style={{ width: 20, height: 20, color: "#4ade80" }} />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ color: "#f1f5f9", fontSize: 26, fontWeight: 700, margin: 0 }}>
            Verify Cases <span style={{ color: "#4ade80" }}>· Maker-Checker Queue</span>
          </h1>
          <p style={{ color: "#94a3b8", margin: "2px 0 0 0", fontSize: 13 }}>
            RBI V-CIP compliant 2-reviewer workflow. A case becomes <strong style={{ color: "#e2e8f0" }}>operational</strong> only after two distinct reviewers approve.
          </p>
        </div>
        <button data-testid="cases-refresh" onClick={fetchCases} disabled={loading}
          style={{ background: "transparent", border: "1px solid #1e2530", color: "#94a3b8", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          {loading ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> : <RefreshCcw style={{ width: 12, height: 12 }} />}
          Refresh
        </button>
        <button data-testid="cases-escalate-accessibility-btn"
          onClick={() => setEscalateOpen(true)}
          style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.18), rgba(168,85,247,0.10))", border: "1px solid rgba(168,85,247,0.45)", color: "#c4b5fd", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
          title="Cannot complete liveness — escalate to human review (not a liveness failure)">
          <Accessibility style={{ width: 13, height: 13 }} />
          Cannot complete liveness
        </button>
      </div>

      {/* Filter pills */}
      <div data-testid="cases-filter-bar" style={{ display: "flex", gap: 8, margin: "16px 0", flexWrap: "wrap" }}>
        {[
          { v: "pending",         lbl: "Pending Maker" },
          { v: "pending-checker", lbl: "Pending Checker" },
          { v: "operational",     lbl: "Operational" },
          { v: "rejected",        lbl: "Rejected" },
          { v: "",                lbl: "All" },
        ].map(f => (
          <button key={f.v}
            data-testid={`cases-filter-${f.v || "all"}`}
            onClick={() => setStatusFilter(f.v)}
            style={{
              background: statusFilter === f.v ? "linear-gradient(135deg, rgba(34,197,94,0.18), rgba(34,197,94,0.10))" : "#0d1117",
              border: `1px solid ${statusFilter === f.v ? "#22c55e" : "#1e2530"}`,
              color: statusFilter === f.v ? "#4ade80" : "#94a3b8",
              borderRadius: 999, padding: "6px 14px", fontSize: 12, fontWeight: 600,
              cursor: "pointer", transition: "all .15s",
            }}>
            {f.lbl}
          </button>
        ))}
      </div>

      {/* Cases table */}
      <div data-testid="cases-list" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 0.6fr 1.3fr 1.3fr 0.5fr", padding: "10px 16px", background: "#080c12", color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #1e2530" }}>
          <div>Reference / Case</div><div>Risk</div><div>Band</div><div>Maker</div><div>Checker</div><div></div>
        </div>
        {cases.length === 0 && !loading && (
          <div style={{ padding: 30, color: "#64748b", fontSize: 13, textAlign: "center" }}>No cases in this queue.</div>
        )}
        {cases.map((c, i) => {
          const st = inferStage(c);
          const col = STAGE_COLORS[st] || STAGE_COLORS.unknown;
          return (
            <div key={c.id}
              data-testid="cases-row"
              onClick={() => openDetail(c)}
              style={{
                display: "grid", gridTemplateColumns: "1.3fr 1fr 0.6fr 1.3fr 1.3fr 0.5fr",
                padding: "12px 16px", borderBottom: i < cases.length - 1 ? "1px solid #1e2530" : "none",
                cursor: "pointer", transition: "background .15s",
                alignItems: "center",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#11161f"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div>
                <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 600 }}>{c.reference}</div>
                <div style={{ color: "#64748b", fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{c.id?.slice(0, 16)}…</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: c.risk_score >= 70 ? "#ef4444" : c.risk_score >= 30 ? "#f59e0b" : "#22c55e", fontSize: 18, fontWeight: 700 }}>{c.risk_score}</span>
                <span style={{ color: "#64748b", fontSize: 10 }}>/100</span>
              </div>
              <div>
                <span style={{ background: col.bg, border: `1px solid ${col.border}`, color: col.text, padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>{col.label.toUpperCase()}</span>
              </div>
              <div style={{ color: c.maker_decision ? "#e2e8f0" : "#475569", fontSize: 12 }}>
                {c.maker_decision ? <>
                  {c.maker_decision === "approve" ? <CheckCircle2 style={{ width: 12, height: 12, color: "#22c55e", display: "inline", marginRight: 4 }} /> : <XCircle style={{ width: 12, height: 12, color: "#ef4444", display: "inline", marginRight: 4 }} />}
                  <strong>{c.maker_decision}</strong> <span style={{ color: "#64748b", fontSize: 10 }}>· {c.maker_email}</span>
                </> : <span>—</span>}
              </div>
              <div style={{ color: c.checker_decision ? "#e2e8f0" : "#475569", fontSize: 12 }}>
                {c.checker_decision ? <>
                  {c.checker_decision === "approve" ? <CheckCircle2 style={{ width: 12, height: 12, color: "#22c55e", display: "inline", marginRight: 4 }} /> : <XCircle style={{ width: 12, height: 12, color: "#ef4444", display: "inline", marginRight: 4 }} />}
                  <strong>{c.checker_decision}</strong> <span style={{ color: "#64748b", fontSize: 10 }}>· {c.checker_email}</span>
                </> : <span>—</span>}
              </div>
              <div><Eye style={{ width: 14, height: 14, color: "#475569" }} /></div>
            </div>
          );
        })}
      </div>

      {/* Detail drawer */}
      {selected && detail && (
        <div data-testid="case-detail-overlay"
          onClick={() => setSelected(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 50, display: "flex", justifyContent: "flex-end" }}
        >
          <div onClick={e => e.stopPropagation()}
            data-testid="case-detail"
            style={{ width: "min(960px, 92vw)", height: "100vh", overflowY: "auto", background: "#0a0e16", borderLeft: "1px solid #1e2530", padding: 26 }}
          >
            {/* Drawer header */}
            <div className="flex items-center" style={{ justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div style={{ color: "#94a3b8", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Case {detail.id?.slice(0, 14)}…</div>
                <h2 style={{ color: "#f1f5f9", fontSize: 22, fontWeight: 700, margin: "2px 0" }}>{detail.reference}</h2>
              </div>
              <button data-testid="case-detail-close" onClick={() => setSelected(null)} style={{ background: "transparent", border: "1px solid #1e2530", color: "#94a3b8", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>Close</button>
            </div>

            {/* Stage banner */}
            {(() => {
              const col = STAGE_COLORS[stage] || STAGE_COLORS.unknown;
              return (
                <div data-testid="case-stage-banner" style={{ background: col.bg, border: `1px solid ${col.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ color: col.text, fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>{col.label.toUpperCase()}</div>
                  <div style={{ color: "#94a3b8", fontSize: 11, display: "flex", gap: 12 }}>
                    <span><Clock style={{ width: 11, height: 11, display: "inline", marginRight: 4 }} />{new Date(detail.completed_at || detail.created_at).toLocaleString()}</span>
                    <span>Risk <strong style={{ color: "#e2e8f0" }}>{detail.risk_score}/100</strong> ({detail.band})</span>
                  </div>
                </div>
              );
            })()}

            {/* Side-by-side selfie / ID */}
            <div data-testid="case-images" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
              {[
                { lbl: "Selfie", ref: detail.selfie_ref, icon: User },
                { lbl: `Document (${detail.document_kind || "—"})`, ref: detail.document_ref, icon: FileText },
              ].map((side, k) => (
                <div key={k} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ padding: "8px 12px", borderBottom: "1px solid #1e2530", color: "#94a3b8", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{side.lbl}</div>
                  {side.ref && side.ref.startsWith("http") ? (
                    <img src={side.ref} alt={side.lbl}
                      onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                      style={{ width: "100%", maxHeight: 280, objectFit: "cover", background: "#000" }} />
                  ) : null}
                  <div style={{ display: side.ref && side.ref.startsWith("http") ? "none" : "flex", height: 220, alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8, color: "#475569", fontSize: 12 }}>
                    <ImgIcon style={{ width: 28, height: 28 }} />
                    <span>Image not available</span>
                    <span style={{ color: "#374151", fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{side.ref?.slice(0, 36) || "—"}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Signal contributions */}
            <div data-testid="case-signals" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 16, marginBottom: 18 }}>
              <h3 style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700, margin: "0 0 12px 0" }}>Signal Contributions (higher bar = lower risk)</h3>
              {(detail.contributions || []).length === 0 && <div style={{ color: "#64748b", fontSize: 12 }}>No contributions recorded.</div>}
              {(detail.contributions || []).map((c, k) => (
                <SignalBar key={k} label={c.signal} score={c.score} weight={c.weight} contribution={c.contribution} />
              ))}
              {(detail.hard_fail_reasons || []).length > 0 && (
                <div style={{ marginTop: 14, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.30)", borderRadius: 8, padding: 10 }}>
                  <div style={{ color: "#fca5a5", fontSize: 11, fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                    <AlertOctagon style={{ width: 12, height: 12 }} />HARD FAILS
                  </div>
                  {detail.hard_fail_reasons.map((r, k) => (
                    <div key={k} style={{ color: "#fecaca", fontSize: 12, padding: "2px 0", fontFamily: "ui-monospace, monospace" }}>· {r}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Document & meta */}
            <div data-testid="case-meta" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 16, marginBottom: 18 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12 }}>
                <div><Hash style={{ width: 11, height: 11, display: "inline", marginRight: 5, color: "#64748b" }} /><span style={{ color: "#94a3b8" }}>Document</span> · <strong style={{ color: "#e2e8f0" }}>{detail.document_kind || "—"}</strong></div>
                <div><span style={{ color: "#94a3b8" }}>Masked ID</span> · <strong style={{ color: "#e2e8f0", fontFamily: "ui-monospace, monospace" }}>{detail.id_masked || "—"}</strong></div>
                <div><span style={{ color: "#94a3b8" }}>Started</span> · {new Date(detail.started_at).toLocaleString()}</div>
                <div><span style={{ color: "#94a3b8" }}>Duration</span> · {detail.duration_ms}ms</div>
                <div><span style={{ color: "#94a3b8" }}>Consent</span> · {detail.consent?.given ? "given" : "MISSING"}</div>
                <div><span style={{ color: "#94a3b8" }}>Models</span> · {Object.values(detail.model_versions || {}).join(" / ")}</div>
              </div>
            </div>

            {/* Maker-Checker action panel */}
            <div data-testid="case-action-panel" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 16 }}>
              <h3 style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700, margin: "0 0 8px 0", display: "flex", alignItems: "center", gap: 8 }}>
                <ShieldAlert style={{ width: 14, height: 14, color: "#a78bfa" }} />
                Maker-Checker Review
                <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 11 }}>
                  {stage === "pending-maker" ? "Stage 1 of 2 · Maker" : stage === "pending-checker" ? "Stage 2 of 2 · Checker" : "Closed"}
                </span>
              </h3>

              {/* Existing maker info */}
              {detail.maker_decision && (
                <div style={{ background: "#080c12", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12 }}>
                  <div style={{ color: "#94a3b8", fontSize: 11, marginBottom: 2 }}>MAKER · {detail.maker_email}</div>
                  <div style={{ color: detail.maker_decision === "approve" ? "#4ade80" : "#fca5a5" }}>
                    <strong>{detail.maker_decision.toUpperCase()}</strong> — {detail.maker_reason}
                  </div>
                  <div style={{ color: "#64748b", fontSize: 10, marginTop: 2 }}>{new Date(detail.maker_at).toLocaleString()}</div>
                </div>
              )}
              {detail.checker_decision && (
                <div style={{ background: "#080c12", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12 }}>
                  <div style={{ color: "#94a3b8", fontSize: 11, marginBottom: 2 }}>CHECKER · {detail.checker_email}</div>
                  <div style={{ color: detail.checker_decision === "approve" ? "#4ade80" : "#fca5a5" }}>
                    <strong>{detail.checker_decision.toUpperCase()}</strong> — {detail.checker_reason}
                  </div>
                  <div style={{ color: "#64748b", fontSize: 10, marginTop: 2 }}>{new Date(detail.checker_at).toLocaleString()}</div>
                </div>
              )}

              {!canIDecide && stage !== "operational" && (stage === "rejected-by-maker" || stage === "rejected-by-checker") && (
                <div style={{ color: "#94a3b8", fontSize: 12, padding: 10, background: "#080c12", borderRadius: 8 }}>
                  Case is closed (terminal stage). No further action available.
                </div>
              )}

              {!canIDecide && stage === "pending-checker" && detail.maker_user_id === me?.id && (
                <div data-testid="case-distinct-warn" style={{ color: "#fbbf24", fontSize: 12, padding: 10, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)", borderRadius: 8 }}>
                  <strong>Maker-Checker rule:</strong> you already acted as the Maker on this case. A distinct second reviewer must approve to mark it operational (RBI V-CIP requirement).
                </div>
              )}

              {canIDecide && (
                <>
                  <textarea
                    data-testid="case-reason-input"
                    value={reason} onChange={e => setReason(e.target.value)}
                    rows={2} placeholder="Reason for your decision (required, min 3 chars) — visible in audit log"
                    style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: "10px 12px", color: "#f1f5f9", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit", marginBottom: 10 }}
                  />
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      data-testid="case-approve-btn"
                      onClick={() => decide("approve")} disabled={acting || reason.trim().length < 3}
                      style={{ flex: 1, background: acting ? "#1e2530" : "linear-gradient(135deg, #16a34a, #15803d)", color: "white", border: "none", borderRadius: 8, padding: "11px 18px", fontSize: 13, fontWeight: 700, cursor: acting ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: reason.trim().length < 3 ? 0.5 : 1 }}>
                      {acting ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <CheckCircle2 style={{ width: 13, height: 13 }} />}
                      Approve as {stage === "pending-maker" ? "Maker" : "Checker"}
                    </button>
                    <button
                      data-testid="case-reject-btn"
                      onClick={() => decide("reject")} disabled={acting || reason.trim().length < 3}
                      style={{ flex: 1, background: acting ? "#1e2530" : "linear-gradient(135deg, #dc2626, #b91c1c)", color: "white", border: "none", borderRadius: 8, padding: "11px 18px", fontSize: 13, fontWeight: 700, cursor: acting ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: reason.trim().length < 3 ? 0.5 : 1 }}>
                      <XCircle style={{ width: 13, height: 13 }} />
                      Reject as {stage === "pending-maker" ? "Maker" : "Checker"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Accessibility escalation modal */}
      {escalateOpen && (
        <div data-testid="escalate-accessibility-overlay"
          onClick={() => setEscalateOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()}
            data-testid="escalate-accessibility-modal"
            style={{ width: "min(520px, 92vw)", background: "#0a0e16", border: "1px solid #1e2530", borderRadius: 12, padding: 24 }}>
            <div className="flex items-center" style={{ gap: 10, marginBottom: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.40)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Accessibility style={{ width: 18, height: 18, color: "#c4b5fd" }} />
              </div>
              <div>
                <h2 style={{ color: "#f1f5f9", fontSize: 17, fontWeight: 700, margin: 0 }}>Accessibility Escalation</h2>
                <p style={{ color: "#94a3b8", fontSize: 11, margin: "2px 0 0 0" }}>
                  RBI V-CIP §6.6 — alternate channel for customers who cannot complete liveness. <strong style={{ color: "#c4b5fd" }}>This is NOT counted as a liveness failure.</strong>
                </p>
              </div>
            </div>
            <label style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Customer reference</label>
            <input data-testid="escalate-ref-input"
              value={escRef} onChange={e => setEscRef(e.target.value)}
              placeholder="e.g. cust_42 or external customer ref"
              style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: "10px 12px", color: "#f1f5f9", fontSize: 13, outline: "none", margin: "6px 0 12px 0" }} />
            <label style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Sub-reason (officer-supplied)</label>
            <textarea data-testid="escalate-subreason-input"
              value={escSubReason} onChange={e => setEscSubReason(e.target.value)}
              rows={3}
              placeholder='e.g. "low-vision customer using screen reader; cannot complete head-pose challenges"'
              style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: "10px 12px", color: "#f1f5f9", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit", margin: "6px 0 16px 0" }} />
            <div style={{ display: "flex", gap: 10 }}>
              <button data-testid="escalate-cancel-btn"
                onClick={() => setEscalateOpen(false)}
                style={{ flex: 1, background: "transparent", border: "1px solid #1e2530", color: "#94a3b8", borderRadius: 8, padding: "10px 16px", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
              <button data-testid="escalate-submit-btn"
                onClick={submitEscalation} disabled={escalating || escRef.trim().length < 2 || escSubReason.trim().length < 3}
                style={{ flex: 1, background: escalating ? "#1e2530" : "linear-gradient(135deg, #9333ea, #7e22ce)", color: "white", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: escalating ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: (escRef.trim().length < 2 || escSubReason.trim().length < 3) ? 0.5 : 1 }}>
                {escalating ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Accessibility style={{ width: 13, height: 13 }} />}
                Escalate to human review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
